import { useEffect, useReducer } from 'react';
import { SWRResponse } from 'swr';
import { ListResponse } from '@tlx/astro-shared';
import { EmployeeDTO } from '@Models/model.types';
import {
    addEmployeesReducer,
    AddEmployeeState,
    SelectAllState,
    stateContainsAllEmployees,
    stateContainsEmployee,
    stateContainsNoEmployees,
    stateContainsSomeButNotAllEmployees,
    stateSelectAllState,
} from '../selectAllReducer';

export interface SelectableMachinery {
    state: AddEmployeeState;
    allChecked: boolean;
    someChecked: boolean;
    selectAllState: SelectAllState;
    isRowChecked: (employeeId: number) => boolean;
    onCheckAll: () => void;
    onCheckRow: (employeeId: number) => void;
    onCheckRows: (employeeIds: string[]) => void;
    onUncheckAll: () => void;
}

export const useSelectableEmployees = (selectable: SWRResponse<ListResponse<EmployeeDTO>>): SelectableMachinery => {
    const [state, updateState] = useReducer(addEmployeesReducer, {
        selectedEmployees: [],
        selectableEmployees: [],
    });

    useEffect(() => {
        if (!selectable.isLoading) {
            updateState({ type: 'SelectAllLoading', value: false });
            if (selectable.data) {
                updateState({
                    type: 'SetSelectableEmployees',
                    employeeIds: selectable.data.values.map((e) => e.id),
                });
            }
            //you have previously clicked selectAll, but it wasn't able to as it was loading
            if (state.clickedButSelectAllWasLoading) {
                updateState({ type: 'SelectAll' });
            }
        } else {
            updateState({ type: 'SelectAllLoading', value: true });
        }
    }, [selectable.isLoading, selectable.data, state.clickedButSelectAllWasLoading]);

    const checkAllHandler = () => {
        if (stateContainsNoEmployees(state)) {
            updateState({
                type: 'SelectAll',
            });
        } else {
            updateState({
                type: 'UncheckAllEmployees',
            });
        }
    };

    const checkRowHandler = (employeeId: number) => {
        if (stateContainsEmployee(state, employeeId)) {
            updateState({
                type: 'UncheckSingleEmployee',
                employeeId,
            });
        } else {
            updateState({
                type: 'CheckSingleEmployee',
                employeeId,
            });
        }
    };

    const checkRowsHandler = (employeeIds: string[]) => {
        uncheckAllHandler();
        employeeIds.forEach((employeeId) => {
            updateState({
                type: 'CheckSingleEmployee',
                employeeId: parseInt(employeeId),
            });
        });
    };

    const rowChecked = (employeeId: number) => stateContainsEmployee(state, employeeId);

    const uncheckAllHandler = () => {
        updateState({
            type: 'UncheckAllEmployees',
        });
    };

    return {
        state,
        allChecked: stateContainsAllEmployees(state),
        someChecked: stateContainsSomeButNotAllEmployees(state),
        selectAllState: stateSelectAllState(state),
        isRowChecked: rowChecked,
        onCheckAll: checkAllHandler,
        onCheckRow: checkRowHandler,
        onCheckRows: checkRowsHandler,
        onUncheckAll: uncheckAllHandler,
    };
};
